import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Button, Select, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService, {
  VENDOR_MULTIPLE_ACCOUNTS,
  VENDOR_OTP_RESTART_CODES,
  VendorOtpChallenge,
} from '../../services/authService';
import { validateRedirectUrl } from '../../utils/redirectValidator';
import AuthShell from '../AuthShell/AuthShell';
import {
  PhoneIcon, ChevronIcon, ShieldIcon, ArrowLeftIcon,
} from '../AuthShell/icons';
import { LOGIN_ROUTE } from '../../constants/routes';

const OTP_LENGTH = 6;

interface CompanyChoice {
  id: number;
  name: string;
}

const RequiredLabel = ({ text }: { text: string }) => (
  <span>{text} <span className="login-required">*</span></span>
);

/** "4:05" — the shape a countdown is read in. */
const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * Vendor sign-in: mobile number, then the OTP texted to it.
 *
 * Vendors hold no platform password, so possession of the registered handset
 * is the credential. The screen is two steps on one route rather than two
 * routes: the challenge only lives in memory, and a reload has to start over
 * anyway.
 */
const VendorLogin = () => {
  const [mobileForm] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [challenge, setChallenge] = useState<VendorOtpChallenge | null>(null);
  const [companies, setCompanies] = useState<CompanyChoice[] | null>(null);
  const [otp, setOtp] = useState('');
  /** Bumped to remount the OTP boxes — antd's Input.OTP keeps the typed cells
   *  internally, and a remount is the one way to clear them that cannot fight
   *  with what the user is typing. */
  const [otpKey, setOtpKey] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  /** Both countdowns are read off wall-clock deadlines, so a throttled or
   *  backgrounded tab cannot leave them showing more time than is left. */
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  /** Guards the auto-submit: typing the sixth digit fires the verify itself. */
  const verifyingRef = useRef(false);

  const clearOtpInput = useCallback(() => {
    setOtp('');
    setOtpKey((key) => key + 1);
  }, []);

  const step: 'mobile' | 'otp' = challenge ? 'otp' : 'mobile';

  useEffect(() => {
    if (step !== 'otp') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [step]);

  const secondsLeft = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const otpExpired = step === 'otp' && secondsLeft === 0;

  /** Start (or restart) the countdowns for a freshly issued challenge. */
  const armChallenge = useCallback((issued: VendorOtpChallenge) => {
    const at = Date.now();
    setChallenge(issued);
    setExpiresAt(at + issued.expiresInSec * 1000);
    setResendAt(at + issued.resendAfterSec * 1000);
    setNow(at);
    clearOtpInput();
  }, [clearOtpInput]);

  /** Back to the mobile step, keeping what the user typed. */
  const restart = useCallback((reason: string) => {
    setChallenge(null);
    clearOtpInput();
    setInfoMsg('');
    setErrorMsg(reason);
  }, [clearOtpInput]);

  /** Step 1 — request the OTP. */
  const handleSendOtp = async (values: any) => {
    setSending(true);
    setErrorMsg('');
    setInfoMsg('');
    try {
      const issued = await authService.vendorSendOtp({
        mobile: String(values.mobile ?? '').trim(),
        company_id: values.company_id ?? null,
      });
      armChallenge(issued);
      setCompanies(null);
      setInfoMsg(`OTP sent to ${issued.maskedMobile}`);
    } catch (error: any) {
      if (error?.code === VENDOR_MULTIPLE_ACCOUNTS) {
        // The number is registered in several companies — no OTP has gone out
        // yet; show the chooser and let the user submit again.
        setCompanies(error?.data?.companies ?? []);
        setErrorMsg(error.message);
      } else {
        setErrorMsg(error.message || 'Unable to send the OTP. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  /** Step 1a — a fresh code on the same challenge. */
  const handleResend = async () => {
    if (!challenge || resendIn > 0 || resending) return;
    setResending(true);
    setErrorMsg('');
    try {
      const issued = await authService.vendorResendOtp(challenge.requestId);
      armChallenge({ ...challenge, ...issued });
      setInfoMsg(`A new OTP has been sent to ${issued.maskedMobile}`);
    } catch (error: any) {
      if (VENDOR_OTP_RESTART_CODES.includes(error?.code)) {
        restart(error.message);
      } else {
        setErrorMsg(error.message || 'Unable to resend the OTP. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  /** Step 2 — redeem the code, then hand off exactly as the password login does. */
  const handleVerify = useCallback(async (code: string) => {
    if (!challenge || verifyingRef.current) return;
    if (code.length !== OTP_LENGTH) {
      setErrorMsg(`Enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }

    verifyingRef.current = true;
    setVerifying(true);
    setErrorMsg('');
    setInfoMsg('');
    try {
      await authService.vendorVerifyOtp({ requestId: challenge.requestId, otp: code });
      message.success('Login successful');

      const rawRedirectUrl = searchParams.get('redirectUrl') || localStorage.getItem('redirect_url');
      const defaultUrl = process.env.REACT_APP_WEBFRONT_URL || 'http://localhost:4800/universe';
      const destination = validateRedirectUrl(rawRedirectUrl, defaultUrl);
      localStorage.removeItem('redirect_url');
      const handoffToken = await authService.generateHandoffToken();

      const separator = destination.includes('?') ? '&' : '?';
      window.location.href = handoffToken
        ? `${destination}${separator}handoffToken=${handoffToken}`
        : destination;
    } catch (error: any) {
      clearOtpInput();
      if (VENDOR_OTP_RESTART_CODES.includes(error?.code)) {
        restart(error.message);
      } else {
        setErrorMsg(error.message || 'Could not verify the OTP. Please try again.');
      }
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
  }, [challenge, clearOtpInput, restart, searchParams]);

  /** Every keystroke — drives the Verify button's enabled state. */
  const handleOtpInput = (cells: string[]) => {
    setOtp(cells.map((cell) => cell || '').join(''));
    setErrorMsg('');
  };

  /** antd fires this only once every cell is filled: the sixth digit is the
   *  submit, so there is nothing left for the user to press. */
  const handleOtpComplete = (value: string) => {
    setOtp(value);
    if (!otpExpired) {
      handleVerify(value);
    }
  };

  /** Back to the employee screen, preserving ?redirectUrl for the round trip. */
  const backToLogin = () => {
    const search = searchParams.toString();
    navigate(search ? `${LOGIN_ROUTE}?${search}` : LOGIN_ROUTE);
  };

  const companyOptions = useMemo(
    () => (companies ?? []).map((c) => ({ value: c.id, label: c.name })),
    [companies],
  );

  /* -------------------------------- OTP step -------------------------------- */
  if (step === 'otp' && challenge) {
    return (
      <AuthShell tagline="Vendor sign-in — verify your mobile number">
        <div className="login-state">
          <div className="login-state-icon"><ShieldIcon /></div>
          <div className="login-state-title">Enter the OTP</div>
          <div className="login-state-text">
            We have sent a {OTP_LENGTH}-digit code to <strong>{challenge.maskedMobile}</strong>
            {challenge.name ? <> for <strong>{challenge.name}</strong></> : null}.
          </div>
        </div>

        {errorMsg ? <div className="login-alert">{errorMsg}</div> : null}
        {!errorMsg && infoMsg ? <div className="login-note">{infoMsg}</div> : null}

        <div className="login-otp-field">
          <Input.OTP
            key={otpKey}
            length={OTP_LENGTH}
            autoFocus
            disabled={verifying || otpExpired}
            formatter={(str) => str.replace(/\D/g, '')}
            onInput={handleOtpInput}
            onChange={handleOtpComplete}
          />
        </div>

        <div className="login-otp-meta">
          {otpExpired ? (
            <span className="login-otp-expired">This OTP has expired</span>
          ) : (
            <span>Expires in {formatCountdown(secondsLeft)}</span>
          )}
          {resendIn > 0 ? (
            <span>Resend in {formatCountdown(resendIn)}</span>
          ) : (
            <button
              type="button" className="login-link"
              onClick={handleResend} disabled={resending}
            >
              {resending ? 'Sending…' : 'Resend OTP'}
            </button>
          )}
        </div>

        <Button
          type="primary" className="login-submit-btn"
          loading={verifying} disabled={otp.length !== OTP_LENGTH || otpExpired}
          onClick={() => handleVerify(otp)}
        >
          Verify &amp; Sign in
        </Button>

        <button
          type="button" className="login-back-link"
          onClick={() => restart('')}
        >
          <ArrowLeftIcon /> Change mobile number
        </button>
      </AuthShell>
    );
  }

  /* ------------------------------ Mobile step ------------------------------- */
  return (
    <AuthShell tagline="Vendor sign-in — we'll text you a one-time password">
      <Form
        form={mobileForm} name="vendor-login" layout="vertical" className="login-form"
        onFinish={handleSendOtp} autoComplete="off"
      >
        {errorMsg ? <div className="login-alert">{errorMsg}</div> : null}

        <Form.Item
          label={<RequiredLabel text="Mobile Number" />} name="mobile"
          rules={[
            { required: true, message: 'Mobile number is required' },
            {
              validator: (_, value) => {
                const digits = String(value ?? '').replace(/\D/g, '');
                if (!digits || /^[6-9]\d{9}$/.test(digits.slice(-10))) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Enter a valid 10-digit mobile number'));
              },
            },
          ]}
        >
          <Input
            prefix={<span className="login-mobile-prefix"><PhoneIcon /> +91</span>}
            placeholder="Enter your registered mobile number"
            inputMode="numeric" maxLength={10}
            onChange={(e) => {
              // Keep the field digits-only so the validator and the backend
              // see the same value the user believes they typed.
              const digits = (e?.target?.value ?? '').replace(/\D/g, '').slice(0, 10);
              mobileForm.setFieldsValue({ mobile: digits });
            }}
          />
        </Form.Item>

        {companies ? (
          <>
            <Form.Item
              label={<RequiredLabel text="Company" />} name="company_id"
              className="login-select-field"
              rules={[{ required: true, message: 'Company is required' }]}
            >
              <Select
                showSearch placeholder="Select Company"
                optionFilterProp="children"
                suffixIcon={<ChevronIcon />}
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={companyOptions}
              />
            </Form.Item>
            <div className="login-field-hint">
              This mobile number is registered with more than one company
            </div>
          </>
        ) : null}

        <Button
          type="primary" htmlType="submit"
          loading={sending} className="login-submit-btn login-submit-btn--spaced"
        >
          Send OTP
        </Button>

        <button type="button" className="login-back-link" onClick={backToLogin}>
          <ArrowLeftIcon /> Back to employee sign in
        </button>
      </Form>
    </AuthShell>
  );
};

export default VendorLogin;
